import Logo from "./header-logo";
import HomeIcon from "@/assets/svgs/home";
import CompassIcon from "@/assets/svgs/compass";
import FoldersIcon from "@quest/assets/svgs/folders";
import MapIcon from "@/assets/svgs/map";
import PassportIcon from "@/assets/svgs/passports";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/tooltip";
import { useState } from "react";
import {
  Bell,
  FileUp,
  Gamepad,
  Globe,
  List,
  Menu,
  Notebook,
  X,
} from "lucide-react";
import { useGetNotificationUnreadCount } from "@/libs/hooks/use-user";
import ResultIcon from "@quest/assets/svgs/result";

function NavigationBar({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation("Home");

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navigate = useNavigate();

  const { data: notificationUnreadCount } = useGetNotificationUnreadCount();

  const handleClick = () => {
    navigate("/");
  };

  const menuItems = [
    {
      id: 1,
      logo: <HomeIcon />,
      href: "/",
      name: t("home"),
    },

    {
      id: 2,
      logo: <CompassIcon />,
      href: "/my-quest",
      name: t("myQuest"),
    },

    {
      id: 4,
      logo: <MapIcon />,
      href: "/icares-tools",
      name: t("testAndTools"),
    },
    {
      id: 5,
      logo: <PassportIcon />,
      href: "/passport",
      name: t("passport"),
    },
    {
      id: 6,
      logo: <Bell className="w-6 h-6" />,
      badge: notificationUnreadCount?.data,
      href: "/announcements",
      name: t("Announcements:notifications"),
    },
    {
      id: 7,
      logo: <Notebook />,
      href: "/journal",
      name: t("journal"),
    },
    {
      id: 8,
      logo: <FoldersIcon />,
      href: "/folders",
      name: t("Folders:folders"),
    },
    {
      id: 9,
      logo: <Globe />,
      href: "/ferrypedia",
      name: t("ferrypedia"),
    },
    {
      id: 10,
      logo: <Gamepad />,
      href: "/games",
      name: t("games"),
    },
    {
      id: 11,
      logo: <FileUp />,
      href: "/documents",
      name: t("DocumentUpload:uploadYourDocuments"),
    },
    {
      id: 12,
      logo: <ResultIcon />,
      href: "/test-results?test=beroepentest",
      name: t("testResults"),
    },
    {
      id: 13,
      logo: <List />,
      href: "/occupation-and-education-lists",
      name: `${t("Passport:education")} & ${t("Passport:occupations")}`,
    },
  ];

  return (
    <>
      <div className="flex">
        <div className="hidden xl:flex  flex-col col-span-1 mr-[40px]">
          <Logo handleClick={handleClick} />
          <div
            id="navigation-menu-student"
            className="bg-[#f49b4b] fixed top-[8.5rem] z-50 p-2 w-fit flex flex-col gap-3 items-center max-h-[calc(100vh-15rem)] overflow-y-auto h-fit rounded-[100px]"
          >
            {menuItems.map((item) => (
              <TooltipProvider key={item.id}>
                <Tooltip>
                  <TooltipTrigger>
                    <NavLink
                      to={item.href}
                      className="flex items-center relative gap-2 p-4 cursor-pointer hover:bg-[#FFD1A5] hover:rounded-[100px] transition-all duration-300 ease-in-out"
                    >
                      {item.logo}
                      {item.badge != null && item.badge > 0 && (
                        <span className="text-[12px] absolute top-0 right-0 font-bold bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent className="ml-3">
                    <div className="text-center text-[12px] font-bold ">
                      {item.name}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>

        {/* Mobile Hamburger Menu (Tablet and below) */}
        <div
          className={`xl:hidden fixed top-4 ${
            !isMenuOpen ? "right-4" : "left-4"
          } z-50`}
        >
          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="  bg-white p-2 rounded-md shadow-md"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} color="black" />}
          </button>
        </div>

        {/* Mobile Dropdown Drawer */}
        {isMenuOpen && (
          <div className="fixed overflow-auto h-full top-0 left-0 w-fit  overflow-y-auto bg-[#f49b4b]  z-40 p-6 flex flex-col gap-6 shadow-lg">
            <Logo />
            {menuItems.map((item) => (
              <NavLink
                key={item.id}
                to={item.href}
                onClick={() => setIsMenuOpen(false)}
                className="flex mt-4 items-center gap-3 p-3 hover:bg-[#FFD1A5] rounded-md "
              >
                {item.logo}
                <span className="font-bold">{item.name}</span>
                {item.badge > 0 && (
                  <span className="text-[12px] font-bold bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        )}

        <div className="w-[100%]  ">{children}</div>
      </div>
    </>
  );
}

export default NavigationBar;
